import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import carParking from "../assets/lottie-files/carParking.lottie";
import parkingImg from "../assets/MarketParkingPlan-WEB-2.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: "2rem" }}>
    <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)", fontWeight: 800, color: "#0d1b2a", margin: 0 }}>
      {children}
    </h2>
    <div style={{ width: 56, height: 4, background: "linear-gradient(90deg, #6dc744, #4a8c25)", borderRadius: 2, marginTop: 8 }} />
  </div>
);

const parkingLots = [
  {
    name: "Lot 1",
    color: "#6dc744",
    icon: "🟢",
    description: "8 vendor bays, or 16 parking spaces. Reserved for Market use only.",
  },
  {
    name: "Lot 2",
    color: "#9C27B0",
    icon: "🟣",
    description: "Approximately 34 spaces, available weekdays after 4:30 PM, and all day Saturday & Sunday.",
  },
  {
    name: "Lot 3",
    color: "#c6252d",
    icon: "🔴",
    description: "43 spaces total. 10 spaces reserved for Market use (yellow), 33 open to the public. On Wednesdays after 12:00 PM, all spaces are signed for Market use.",
  },
  {
    name: "Lot 4 (CRA Tax Building)",
    color: "#2d6a14",
    icon: "🌲",
    description: "160+ spaces available Saturdays, May–December, 7:00 AM – 7:00 PM. Also available the 3rd Saturday of each month, January–April, 7:00 AM – 7:00 PM.",
  },
  {
    name: "IM — Accessible Parking",
    color: "#1976D2",
    icon: "♿",
    description: "Designated impaired mobility parking spaces, close to the entrance.",
  },
  {
    name: "AY — Active Yard",
    color: "#C19A3B",
    icon: "🚧",
    description: "Restricted area for staff and deliveries only. No public access at any time.",
  },
];

const infoCards = [
  {
    icon: "♿",
    title: "Accessibility",
    accent: "#6dc744",
    body: "The Market is one of the most accessible buildings in St. John's. You'll find wheelchair accessible aisles and doors, indoor/outdoor seating, and 11 gender-neutral washrooms.",
  },
  {
    icon: "💳",
    title: "How to Pay",
    accent: "#c6252d",
    body: "Check vendor profile pages for payment details. The Market has two ATMs available on-site, and all user fees go directly towards operating costs for the Market building.",
  },
  {
    icon: "🕘",
    title: "Market Hours",
    accent: "#C19A3B",
    body: "Open every Saturday year-round, 9:00 AM – 4:00 PM. Some Sundays also available — check our social media for details.",
  },
];

const PlanYourVisit: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div>
      {/* HERO */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span variants={fadeUp} style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}>
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Visitor Guide
            </motion.span>

            <motion.h1 variants={fadeUp} style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}>
              Plan Your{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Visit
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}>
              Everything you need to know about parking, accessibility, and making your Market trip easy and enjoyable.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a
                href="https://www.google.com/maps/place/245+Freshwater+Rd"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s", display: "inline-block", textDecoration: "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
              >
                Get Directions →
              </a>
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.88 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.85, ease: "easeOut" }} style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}>
            <DotLottieReact src={carParking} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* QUICK INFO CARDS */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: "1.5rem" }}>
            {infoCards.map(({ icon, title, accent, body }) => (
              <motion.div key={title} variants={fadeUp} style={{ height: "100%" }}>
                <LightCard style={{ borderTop: `4px solid ${accent}`, height: "100%" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>{icon}</div>
                  <h3 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0d1b2a", marginBottom: "0.5rem" }}>{title}</h3>
                  <p style={{ color: "#4b5563", lineHeight: 1.7, margin: 0, fontSize: "0.97rem" }}>{body}</p>
                </LightCard>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PARKING MAP */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <SectionHeading>Parking Map</SectionHeading>
            </motion.div>

            <motion.div variants={fadeUp} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 500px), 1fr))", gap: "2.5rem", alignItems: "start" }}>
              {/* Map image */}
              <div>
                <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>
                  <img src={parkingImg} alt="SJFM Parking Plan" style={{ width: "100%", display: "block" }} />
                </div>
                <p style={{ marginTop: "0.6rem", fontSize: "0.88rem", color: "#6b7280", textAlign: "center" }}>
                  Parking lots available around the St. John's Farmers' Market.
                </p>
              </div>

              {/* Parking lot accordion */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
                {parkingLots.map((lot, index) => {
                  const isOpen = openIndex === index;
                  return (
                    <div
                      key={index}
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      style={{
                        background: "white",
                        borderRadius: 14,
                        border: `1px solid ${isOpen ? lot.color + "55" : "rgba(45,106,20,0.1)"}`,
                        borderLeft: `4px solid ${lot.color}`,
                        boxShadow: isOpen ? `0 4px 18px ${lot.color}20` : "0 2px 10px rgba(0,0,0,0.04)",
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "box-shadow 0.2s, border-color 0.2s",
                        userSelect: isOpen ? "text" : "none",
                      }}
                    >
                      <div style={{ padding: "0.9rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          <span style={{ fontSize: "1.1rem" }}>{lot.icon}</span>
                          <span style={{ fontWeight: 700, fontSize: "0.97rem", color: "#0d1b2a" }}>{lot.name}</span>
                        </div>
                        <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", background: `${lot.color}18`, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.25s ease", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                          <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                            <path d="M5 7.5L10 12.5L15 7.5" stroke={lot.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <motion.div
                            key="desc"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            style={{ overflow: "hidden" }}
                          >
                            <div style={{ padding: "0 1.2rem 1rem", borderTop: `1px solid ${lot.color}22` }}>
                              <p style={{ margin: "0.6rem 0 0", fontSize: "0.92rem", lineHeight: 1.7, color: "#374151" }}>
                                {lot.description}
                              </p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PlanYourVisit;
